import React from "react";
import {
  MONTHS,
  RECURRENCE_END_TYPES,
  RECURRENCE_PATTERNS,
  RECURRENCE_UNITS,
  WEEKDAYS,
  WEEK_NUMBERS,
} from "./reminderConstants";

/**
 * Calendar-style recurrence editor, modelled on Outlook's recurrence dialog.
 *
 * Only the controls the chosen pattern uses are shown: "every weekday" needs no
 * interval, weekly needs weekdays, and monthly/yearly offer either a fixed day
 * of the month or the ordinal form ("the third Thursday").
 * @param {object} props
 * @param {object} props.value the recurrence form state.
 * @param {(patch: object) => void} props.onChange applies a partial update.
 * @returns {JSX.Element}
 */
export default function RecurrenceEditor({ value, onChange }) {
  const pattern = value.pattern || "daily";
  const isMonthly = pattern === "monthly";
  const isYearly = pattern === "yearly";
  const usesMonthPosition = isMonthly || isYearly;
  const byOrdinal = value.weekNumber !== "" && value.dayOfWeek !== "";

  /** Adds or removes a weekday from the weekly pattern's selection. */
  const toggleWeekday = (day) => {
    const current = value.weekdays || [];
    onChange({
      weekdays: current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day].sort((a, b) => a - b),
    });
  };

  /** Switches monthly/yearly between a fixed day and the ordinal form. */
  const setMonthMode = (ordinal) =>
    onChange(
      ordinal
        ? { monthDay: "", weekNumber: 1, dayOfWeek: 1 }
        : { monthDay: 1, weekNumber: "", dayOfWeek: "" },
    );

  return (
    <div className="rem-recurrence">
      <span className="rem-label">Recurrence</span>

      <div className="rem-recur-row">
        <select
          className="rem-select"
          aria-label="Recurrence pattern"
          value={pattern}
          onChange={(e) => onChange({ pattern: e.target.value })}
        >
          {RECURRENCE_PATTERNS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>

        {pattern !== "weekday" && (
          <span className="rem-recur-inline">
            every
            <input
              className="rem-input rem-narrow"
              type="number"
              min="1"
              aria-label="Repeat interval"
              value={value.interval}
              onChange={(e) => onChange({ interval: e.target.value })}
            />
            {RECURRENCE_UNITS[pattern]}
          </span>
        )}
      </div>

      {pattern === "weekly" && (
        <div className="rem-recur-row">
          {WEEKDAYS.map((d) => (
            <label key={d.value} className="rem-day-chip">
              <input
                type="checkbox"
                checked={(value.weekdays || []).includes(d.value)}
                onChange={() => toggleWeekday(d.value)}
              />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
      )}

      {usesMonthPosition && (
        <div className="rem-recur-block">
          {isYearly && (
            <div className="rem-recur-row">
              <span className="rem-recur-inline">in</span>
              <select
                className="rem-select"
                aria-label="Month"
                value={value.month}
                onChange={(e) => onChange({ month: e.target.value })}
              >
                <option value="">Any month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="rem-recur-row">
            <label className="rem-radio">
              <input
                type="radio"
                name="rem-month-mode"
                checked={!byOrdinal}
                onChange={() => setMonthMode(false)}
              />
              <span>On day</span>
            </label>
            <input
              className="rem-input rem-narrow"
              type="number"
              min="1"
              max="31"
              aria-label="Day of month"
              disabled={byOrdinal}
              value={value.monthDay}
              onChange={(e) => onChange({ monthDay: e.target.value })}
            />
          </div>

          <div className="rem-recur-row">
            <label className="rem-radio">
              <input
                type="radio"
                name="rem-month-mode"
                checked={byOrdinal}
                onChange={() => setMonthMode(true)}
              />
              <span>On the</span>
            </label>
            <select
              className="rem-select"
              aria-label="Week of month"
              disabled={!byOrdinal}
              value={value.weekNumber}
              onChange={(e) => onChange({ weekNumber: e.target.value })}
            >
              {WEEK_NUMBERS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
            <select
              className="rem-select"
              aria-label="Weekday of month"
              disabled={!byOrdinal}
              value={value.dayOfWeek}
              onChange={(e) => onChange({ dayOfWeek: e.target.value })}
            >
              {WEEKDAYS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="rem-recur-row">
        <select
          className="rem-select"
          aria-label="End condition"
          value={value.endType}
          onChange={(e) => onChange({ endType: e.target.value })}
        >
          {RECURRENCE_END_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {value.endType === "afterCount" && (
          <span className="rem-recur-inline">
            <input
              className="rem-input rem-narrow"
              type="number"
              min="1"
              aria-label="Occurrence count"
              value={value.endCount}
              onChange={(e) => onChange({ endCount: e.target.value })}
            />
            occurrence(s)
          </span>
        )}

        {value.endType === "byDate" && (
          <input
            className="rem-input"
            type="date"
            aria-label="End date"
            value={value.endDate}
            onChange={(e) => onChange({ endDate: e.target.value })}
          />
        )}
      </div>
    </div>
  );
}
